import { useEffect, useRef } from "react";
import type { CookieConsentStatus } from "@/app/store/cookieConsentSlice";
import { sendAudienceView } from "./audience";
import { beginPageView, getAnalyticsLocale, pageTypeOf } from "./page";
import { trackEvent, trackOncePerPageView } from "./track";

/** Share of the section, or of the viewport, that must be visible to count a section as seen. */
const SECTION_VISIBLE_SHARE = 0.35;
const SECTION_THRESHOLDS = [0, 0.1, 0.2, 0.35, 0.5, 0.75, 1];
/** Sections can mount late (desktop/phone variants swap after hydration): look again then. */
const SECTION_RESCAN_DELAYS_MS = [1000, 3000];
const DEPTHS = [25, 50, 75, 100] as const;

/**
 * Starts observing every `main section[id]` currently in the DOM (re-observing an element is a
 * no-op). The caller owns the observer and releases it with `observer.disconnect()`.
 */
function observeSections(observer: IntersectionObserver): void {
  document.querySelectorAll<HTMLElement>("main section[id]").forEach((el) => observer.observe(el));
}

/**
 * Page-level website analytics, mounted once by the observability provider:
 * - starts a page view on every route change, first page included (per-view deduplication
 *   restarts, the anonymous audience counter counts it when enabled);
 * - `site-page-view {path, locale, pageType}` once consent is accepted;
 * - `site-section-view {path, section}` for each `main section[id]` seen in this page view;
 * - `site-scroll-depth {path, depth}` at 25/50/75/100 %.
 *
 * Without consent only the (flag-gated, anonymous) audience counter runs: no observer, no
 * scroll listener.
 *
 * @param pathname - Current route (`usePathname()`).
 * @param consent - Current cookie choice.
 */
export function usePageAnalytics(pathname: string, consent: CookieConsentStatus): void {
  const startedFor = useRef<string | null>(null);
  const viewSentFor = useRef<string | null>(null);
  const accepted = consent === "accepted";

  // New page view. The ref keeps StrictMode's double effect from counting it twice.
  useEffect(() => {
    if (startedFor.current === pathname) return;
    startedFor.current = pathname;
    viewSentFor.current = null;
    beginPageView(pathname);
    sendAudienceView();
  }, [pathname]);

  // Page view event, sent as soon as consent exists for this page (including an accept on it).
  useEffect(() => {
    if (!accepted || viewSentFor.current === pathname) return;
    viewSentFor.current = pathname;
    void trackEvent("site-page-view", {
      path: pathname,
      locale: getAnalyticsLocale(),
      pageType: pageTypeOf(pathname),
    });
  }, [accepted, pathname]);

  // Sections seen.
  useEffect(() => {
    if (!accepted || typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const viewport = entry.rootBounds?.height ?? window.innerHeight;
          const seen =
            entry.intersectionRatio >= SECTION_VISIBLE_SHARE ||
            entry.intersectionRect.height >= viewport * SECTION_VISIBLE_SHARE;
          if (!seen) continue;
          const section = (entry.target as HTMLElement).id;
          observer.unobserve(entry.target);
          trackOncePerPageView(`section:${section}`, "site-section-view", {
            path: pathname,
            section,
          });
        }
      },
      { threshold: SECTION_THRESHOLDS },
    );

    const scan = () => observeSections(observer);
    scan();
    const [firstDelay, secondDelay] = SECTION_RESCAN_DELAYS_MS;
    const firstRescan = window.setTimeout(scan, firstDelay);
    const secondRescan = window.setTimeout(scan, secondDelay);
    let resizeTimer = 0;
    const onResize = () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(scan, 500);
    };
    window.addEventListener("resize", onResize, { passive: true });

    return () => {
      observer.disconnect();
      window.clearTimeout(firstRescan);
      window.clearTimeout(secondRescan);
      window.clearTimeout(resizeTimer);
      window.removeEventListener("resize", onResize);
    };
  }, [accepted, pathname]);

  // Scroll depth.
  useEffect(() => {
    if (!accepted) return;

    let frame = 0;
    let next = 0; // index in DEPTHS of the next depth to reach
    const measure = () => {
      frame = 0;
      const doc = document.documentElement;
      const scrollable = doc.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      const percent = ((window.scrollY + window.innerHeight) / doc.scrollHeight) * 100;
      while (next < DEPTHS.length) {
        const depth = DEPTHS[next];
        // 99 for the bottom: sub-pixel rounding keeps some browsers a hair short of 100.
        if (percent < (depth === 100 ? 99 : depth)) break;
        trackOncePerPageView(`depth:${depth}`, "site-scroll-depth", {
          path: pathname,
          depth: String(depth),
        });
        next += 1;
      }
      if (next >= DEPTHS.length) window.removeEventListener("scroll", onScroll);
    };
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(measure);
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [accepted, pathname]);
}
